import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useCampaign } from "../views/Campaign/CampaignContext";
import { usePlace } from "./PlaceProvider";
import { HTTPClient } from "../HTTPClient";

interface ToDoContextValue {
  jeuDone: boolean;
  googleConnected: boolean | null;
  hasCampaigns: boolean | null;
  pendingCount: number;
  totalDone: number;
  total: number;
  isLoading: boolean;
}

const ToDoContext = createContext<ToDoContextValue>({
  jeuDone: false,
  googleConnected: null,
  hasCampaigns: null,
  pendingCount: 0,
  totalDone: 2,
  total: 5,
  isLoading: true,
});

export function ToDoProvider({ children }: { children: ReactNode }) {
  const { isLoaded, actions, gifts } = useCampaign();
  const { selectedPlace } = usePlace();

  const jeuDone = isLoaded ? actions.length > 0 || gifts.length > 0 : false;
  const [googleConnected, setGoogleConnected] = useState<boolean | null>(null);
  const [hasCampaigns, setHasCampaigns] = useState<boolean | null>(null);

  useEffect(() => {
    if (!selectedPlace?.id) {
      setGoogleConnected(null);
      setHasCampaigns(null);
      return;
    }

    // Reset per-place values on place switch to avoid bleeding previous place state.
    setGoogleConnected(null);
    setHasCampaigns(null);

    HTTPClient.get("/reviews/config?id=" + selectedPlace.id)
      .then(({ data }) => setGoogleConnected(!!data?.google))
      .catch(() => setGoogleConnected(false));
    HTTPClient.get("/sms?id=" + selectedPlace.id)
      .then(({ data }) => setHasCampaigns(Array.isArray(data) && data.length > 0))
      .catch(() => setHasCampaigns(false));
  }, [selectedPlace?.id]);

  const isLoading = !isLoaded || googleConnected === null || hasCampaigns === null;

  const FIXED_DONE = 2;
  const total = 5;
  const dynamicDone = (jeuDone ? 1 : 0) + (googleConnected ? 1 : 0) + (hasCampaigns ? 1 : 0);
  const totalDone = FIXED_DONE + dynamicDone;
  const pendingCount = total - totalDone;

  return (
    <ToDoContext.Provider value={{ jeuDone, googleConnected, hasCampaigns, pendingCount, totalDone, total, isLoading }}>
      {children}
    </ToDoContext.Provider>
  );
}

export function useToDo() {
  return useContext(ToDoContext);
}
